/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { Formik, yupToFormErrors } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import ValidationSchema from './EditFormValidation'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import Modal from '../../../../components/Modal'
import Field from '../../../../components/shared/Field'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'

const HalfWidthDiv = styled.div`
  width: 50%;
  padding-right: 23px;
  float: left;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  folderStrctTplType: any
  loading?: Promise<any> | null
}

const EditForm: React.FC<EditFormProps> = ({ onSubmit, doClose, isEdit, folderStrctTplType, loading }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()

  const getDefaults = (vals: any) => {
    const defaults = validationSchema.cast(vals, {
      recursive: true,
      stripUnknown: true,
      context: vals
    })
    return defaults
  }

  const [initialValues, setInitialValues] = useState(() => getDefaults(folderStrctTplType))

  useEffect(() => {
    // Update initial values when folder structure template type data is loaded
    if (isEdit && folderStrctTplType && Object.keys(folderStrctTplType).length > 0) {
      setInitialValues(getDefaults(folderStrctTplType))
    }
  }, [folderStrctTplType, isEdit])

  const closeDialog = () => {
    if (doClose) {
      doClose()
    }
  }

  return (
    <div>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => yupToFormErrors(err)
            )
        }}
        onSubmit={(values, actions) => {
          const finalData = getDefaults(values)
          const type = cloneDeep(finalData)
          return onSubmit(type, actions)
        }}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {({ values, handleSubmit }) => (
          <div>
            <Loader blocking={(isEdit && Object.keys(folderStrctTplType).length === 0) || loading !== null}>
              <Modal.Body>
                <form className={'clearfix'}>
                  <HalfWidthDiv>
                    <StyledFormGroup controlId="5f2e5dfd-34c9-4220-8775-17f7d54d91b6">
                      <Form.Label>{translate('METADATA_FORM_LABEL_NAME')}</Form.Label>
                      <Field
                        type="text"
                        name="name"
                        maxLength={255}
                        autoFocus
                        id="5f2e5dfd-34c9-4220-8775-17f7d54d91b6"
                      />
                      <ErrorMessageStyled name="name" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="f1db725e-91c7-4629-8568-f09436af9f31">
                      <Form.Label>{translate('METADATA_FORM_LABEL_DESCRIPTION')}</Form.Label>
                      <Field type="text" name="description" maxLength={255} id="f1db725e-91c7-4629-8568-f09436af9f31" />
                      <ErrorMessageStyled name="description" />
                    </StyledFormGroup>
                  </HalfWidthDiv>
                </form>
              </Modal.Body>
              <Modal.Footer>
                <button
                  data-action={'cancel'}
                  className="btn btn-chita btn-link"
                  onClick={closeDialog}
                  id="e0e98099-284d-4cb5-b7ce-e797a0e558a8"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  data-action={'save'}
                  className="btn btn-primary text-capitalize"
                  onClick={() => handleSubmit()}
                  id="af8140ae-93b9-4dce-8565-b0ace0ad6589"
                >
                  {translate('SAVE')}
                </button>
              </Modal.Footer>
            </Loader>
          </div>
        )}
      </Formik>
    </div>
  )
}

export default EditForm
